import { FormEvent, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormField } from '../components/FormField';
import { SearchableSelect } from '../components/SearchableSelect';
import { StepActions } from '../components/StepActions';
import { Alert } from '../components/Alert';
import {
  validateCondoUnit,
  validateUtility,
  RegistrationApiError,
} from '../../../../services/registrationApi';
import type { AddressInfo, RegistrationBranch, TowerOption } from '../types';

export interface UnitSelection {
  documentRequired: boolean;
  commercialServiceId?: string;
  unitId?: string;
}

interface UnitStepProps {
  registrationId: string;
  branch: RegistrationBranch;
  address: AddressInfo | null;
  towers: TowerOption[];
  onComplete: (selection: UnitSelection) => void;
  onBack: () => void;
}

/** Cartão de resumo do endereço encontrado. */
function AddressSummary({ address }: { address: AddressInfo | null }) {
  const { t } = useTranslation();
  if (!address) return null;
  const line2 = [address.neighborhood, address.city, address.state].filter(Boolean).join(' · ');
  return (
    <div className="mb-[20px] p-[14px] rounded-[10px] bg-sky-50 border-0 border-l-[4px] border-solid border-sky-500">
      <p className="text-[10px] uppercase tracking-wide text-navy-700 font-bold mb-[2px]">
        {t('views.registration.unit.address_found', 'Endereço encontrado')}
      </p>
      <p className="text-[13px] text-navy-800 font-semibold">
        {address.street}
        {address.number ? `, ${address.number}` : ''}
      </p>
      {line2 && <p className="text-[12px] text-navy-700/80">{line2}</p>}
    </div>
  );
}

export function UnitStep({
  registrationId,
  branch,
  address,
  towers,
  onComplete,
  onBack,
}: UnitStepProps) {
  const { t } = useTranslation();
  // Condomínio
  const [towerId, setTowerId] = useState('');
  const [unitId, setUnitId] = useState('');
  // Concessionária
  const [rgi, setRgi] = useState('');
  const [ownershipCode, setOwnershipCode] = useState('');

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // Fallback: quando a validação falha, oferecemos comprovar por documento.
  const [offerDocFallback, setOfferDocFallback] = useState(false);

  const selectedTower = useMemo(
    () => towers.find((t) => t.id === towerId),
    [towers, towerId],
  );

  const towerOptions = useMemo(
    () => towers.map((t) => ({ value: t.id, label: t.name })),
    [towers],
  );

  const unitOptions = useMemo(
    () =>
      selectedTower?.units.map((u) => ({
        value: u.id,
        label: u.label,
        hint: u.label,
      })) ?? [],
    [selectedTower],
  );

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setOfferDocFallback(false);
    const errs: Record<string, string> = {};

    if (branch === 'condominium') {
      if (!towerId) errs.tower = t('views.registration.unit.select_tower_error', 'Selecione a torre/bloco.');
      if (!unitId) errs.unit = t('views.registration.unit.select_unit_error', 'Selecione a sua unidade.');
    } else {
      if (!rgi.trim()) errs.rgi = t('views.registration.unit.rgi_error', 'Informe o RGI da sua ligação.');
      if (!ownershipCode.trim()) errs.ownership = t('views.registration.unit.ownership_error', 'Informe o código de titularidade.');
    }
    setFieldErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setSubmitting(true);
    try {
      const result =
        branch === 'condominium'
          ? await validateCondoUnit({ registrationId, commercialServiceId: towerId, unitId })
          : await validateUtility({ registrationId, rgi: rgi.trim(), ownershipCode: ownershipCode.trim() });

      if (result.ok) {
        onComplete({
          documentRequired: result.documentRequired,
          commercialServiceId: branch === 'condominium' ? towerId : undefined,
          unitId: branch === 'condominium' ? unitId : undefined,
        });
        return;
      }

      // Validação não confirmou a titularidade → oferece via documento.
      setError(
        result.message ||
          (branch === 'utility'
            ? t('views.registration.unit.validate_utility_error', 'Não conseguimos validar o RGI e o código de titularidade informados.')
            : t('views.registration.unit.validate_unit_error', 'Não conseguimos validar a unidade selecionada.')),
      );
      setOfferDocFallback(true);
    } catch (err) {
      // Residência já cadastrada: não há como comprovar por documento — a
      // unidade tem dono. Mostramos o titular ofuscado e escondemos o fallback.
      if (err instanceof RegistrationApiError && err.code === 'already_registered') {
        setError(
          err.owner
            ? t(
                'views.registration.unit.already_registered',
                'Não é possível continuar o cadastro para a unidade selecionada, pois ela já está cadastrada para o(a) Sr(a) {{name}} ({{email}}).',
                { name: err.owner.name, email: err.owner.email },
              )
            : t(
                'views.registration.unit.already_registered_no_owner',
                'Não é possível continuar: esta unidade já possui um cadastro ativo.',
              ),
        );
        setOfferDocFallback(false);
        return;
      }

      setError(
        err instanceof RegistrationApiError
          ? err.message
          : t('views.registration.unit.validate_generic_error', 'Não foi possível validar. Tente novamente.'),
      );
      setOfferDocFallback(true);
    } finally {
      setSubmitting(false);
    }
  };

  const continueWithDocument = () => {
    onComplete({
      documentRequired: true,
      commercialServiceId: branch === 'condominium' ? towerId : undefined,
      unitId: branch === 'condominium' ? unitId : undefined,
    });
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <AddressSummary address={address} />

      {error && <Alert type="error" message={error} onClose={() => setError('')} />}

      {branch === 'condominium' ? (
        <>
          {/* Torre / bloco */}
          <SearchableSelect
            id="tower-select"
            label={t('views.registration.unit.tower_label', 'Torre / Bloco')}
            icon="fa-building"
            required
            value={towerId}
            onChange={(v) => {
              setTowerId(v);
              setUnitId('');
            }}
            options={towerOptions}
            error={fieldErrors.tower}
            hint={
              towers.length > 8
                ? t('views.registration.unit.search_tower_hint', 'Digite para buscar a torre/bloco.')
                : undefined
            }
            placeholder={t('views.registration.unit.select_placeholder', 'Selecione...')}
          />

          {/* Unidade */}
          <SearchableSelect
            id="unit-select"
            label={t('views.registration.indicator.unit', 'Unidade')}
            icon="fa-home"
            required
            disabled={!selectedTower}
            value={unitId}
            onChange={setUnitId}
            options={unitOptions}
            error={fieldErrors.unit}
            hint={
              selectedTower && selectedTower.units.length > 8
                ? t('views.registration.unit.search_unit_hint', 'Digite o número da unidade para buscar entre {{count}}.', {
                    count: selectedTower.units.length,
                  })
                : undefined
            }
            placeholder={t('views.registration.unit.select_placeholder', 'Selecione...')}
            disabledPlaceholder={t('views.registration.unit.choose_tower_first', 'Escolha a torre primeiro')}
            emptyMessage={t('views.registration.unit.no_unit_match', 'Nenhuma unidade corresponde à busca.')}
          />
        </>
      ) : (
        <>
          <FormField
            label="RGI"
            icon="fa-tint"
            placeholder={t('views.registration.unit.rgi_placeholder', 'Número da sua ligação (RGI)')}
            value={rgi}
            onChange={(e) => setRgi(e.target.value)}
            error={fieldErrors.rgi}
            hint={t('views.registration.unit.rgi_hint', 'Encontrado na sua conta de água/gás.')}
            required
            autoFocus
          />
          <FormField
            label={t('views.registration.unit.ownership_label', 'Código de titularidade')}
            icon="fa-key"
            placeholder={t('views.registration.unit.ownership_placeholder', 'Código enviado pela concessionária')}
            value={ownershipCode}
            onChange={(e) => setOwnershipCode(e.target.value)}
            error={fieldErrors.ownership}
            required
          />
        </>
      )}

      {offerDocFallback && (
        <button
          type="button"
          onClick={continueWithDocument}
          className="w-full mb-2 py-[10px] px-[14px] rounded-[20px] cursor-pointer text-[12px] font-medium text-navy-700 bg-sky-50 border border-solid border-sky-200 hover:bg-sky-200/50 transition-colors"
        >
          <i className="fa fa-id-card mr-2" aria-hidden="true"></i>
          {t('views.registration.unit.prove_with_document', 'Comprovar titularidade enviando um documento')}
        </button>
      )}

      <StepActions
        submitLabel={t('views.registration.unit.submit', 'Validar unidade')}
        loading={submitting}
        loadingLabel={t('views.registration.unit.submitting', 'Validando...')}
        onBack={onBack}
      />
    </form>
  );
}
